import { LABEL } from "./constants";

/** Format a date string as YYYY */
export function fmtYear(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  return new Date(dateStr).getFullYear().toString();
}

/** Format a date string as Qn YYYY (calendar quarter) */
export function fmtQuarter(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `Q${q} ${d.getFullYear()}`;
}

/** Format a fiscal period for tooltip: annual or quarterly */
export function fmtFiscalPeriod(_period_start: string | null | undefined, period_end: string | null | undefined, form_type: string | null | undefined): string {
  if (!period_end) return "";
  if (form_type === "10-K" || form_type === "20-F" || form_type === "40-F") return `FY ${fmtYear(period_end)}`;
  return fmtQuarter(period_end);
}

const TITLE_ACRONYMS = new Set(["ceo", "coo", "cfo", "cto", "cmo", "cio", "cso", "vp", "svp", "evp"]);

/** Convert a string to Title Case (e.g., "JOHN L. DOE" -> "John L. Doe", "COO" -> "COO") */
export function toTitleCase(str: string | null | undefined): string {
  if (!str) return "";
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => {
      const cleanWord = word.replace(/[^a-z]/g, "");
      if (TITLE_ACRONYMS.has(cleanWord)) {
        return word.toUpperCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

/* ═══════════════════════════════════════════════════════════════════════
   Formatting helpers
   ═══════════════════════════════════════════════════════════════════════ */

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$", EUR: "€", GBP: "£", SEK: "kr", NOK: "kr",
  DKK: "kr", CHF: "CHF", PLN: "zł", CZK: "Kč", HUF: "Ft",
  JPY: "¥", TWD: "NT$", ARS: "ARS$",
};

function sym(currencyCode: string): string {
  return CURRENCY_SYMBOLS[currencyCode.toUpperCase()] ?? currencyCode + " ";
}

/** Format a financial value with appropriate unit suffix.
 *  Pass `currency` (e.g. "EUR") for non-USD companies. */
export function fmt(v: number | null | undefined, unit?: string | null, currency?: string | null): string {
  if (v == null) return "N/A";
  
  const u = unit?.trim() || "";
  const numerator = u.split("/")[0].toUpperCase();
  const perShare = u.toLowerCase().includes("/share");

  // Determine if this unit represents a currency.
  // It's a currency if the numerator is in our known currencies list,
  // or if the numerator matches the company's base currency code.
  const isCurrency = (numerator in CURRENCY_SYMBOLS) || (currency && numerator === currency.toUpperCase());
  
  // Also, if the unit is completely missing but we have a company currency, 
  // we assume it's a currency (default behavior for standard financial line items)
  // UNLESS it's a known non-currency like 'pure', 'shares', etc. 
  const isDefaultCurrency = !u && !perShare;

  let s = "";
  if (isCurrency) {
    s = sym(numerator);
  } else if (isDefaultCurrency) {
    s = sym(currency || "USD");
  }

  if (perShare) return s + v.toFixed(2);
  const a = Math.abs(v);
  if (a >= 1e12) return s + (v / 1e12).toFixed(2) + "T";
  if (a >= 1e9) return s + (v / 1e9).toFixed(2) + "B";
  if (a >= 1e6) return s + (v / 1e6).toFixed(1) + "M";
  if (a >= 1e3)
    return s + v.toLocaleString("en-US", { maximumFractionDigits: 0 });
  return s + v.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

/** Format a percentage change with +/- sign */
export function fmtPct(v: number | null | undefined): string {
  if (v == null) return "—";
  return (v >= 0 ? "+" : "") + v.toFixed(1) + "%";
}

/** Format an absolute change value with +/- sign */
export function fmtChange(
  v: number | null | undefined,
  unit?: string | null,
): string {
  if (v == null) return "—";
  return (v >= 0 ? "+" : "") + fmt(v, unit);
}

/** Convert a raw XBRL concept key (possibly dimensional) into a readable label.
 *  e.g. "RevenueFromContractWithCustomerIncludingAssessedTax__LicenseMember"
 *    → "Revenue From Contract With Customer Including Assessed Tax — License"
 */
export function formatConceptKey(key: string): string {
  const parts = key.split("__");
  const spaced = parts.map((part, i) => {
    // Insert space before each uppercase letter that follows a lowercase letter or another uppercase+lowercase sequence
    let s = part.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
    // Strip "Member" suffix from dimensional member parts (not the base concept)
    if (i > 0) s = s.replace(/\s*Member$/, "");
    return s;
  });
  return spaced.join(" — ");
}

/** Get a robust human-readable label for any metric key, applying fallbacks for custom or raw keys */
export function getMetricLabel(key: string): string {
  if (LABEL[key]) return LABEL[key];
  if (key.includes(" ")) return key;
  if (key.includes("__") || (/[a-z]/.test(key) && /[A-Z]/.test(key))) {
    return formatConceptKey(key);
  }
  return toTitleCase(key.replace(/_/g, " "));
}

/** Shared Chart.js default options */
export function chartDefaults() {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: {
        ticks: {
          color: "#a1a1aa",
          maxRotation: 45,
          font: { size: 10, family: "Inter" },
        },
        grid: { color: "rgba(255,255,255,.06)" },
      },
      y: {
        ticks: {
          color: "#a1a1aa",
          font: { size: 10, family: "Inter" },
        },
        grid: { color: "rgba(255,255,255,.06)" },
      },
    },
  };
}
